import * as XLSX from "xlsx";
import fileHelper from "./fileHelper";
import importConfig from "../../../../../../../../config/config.import";
import fireDb from "../../../../../../../../api/firebase/realtime/utils";
import { singleImportFile } from "../../../../../../service/importService";

const safeFunc = () => null;

const processExcelFile = async (buffer, state) => {
  try {
    const patternSetting = importConfig.pattern.hsna_sna;
    // ArrayBuffer
    if (buffer?.constructor?.name !== "ArrayBuffer") {
      return { data: null, error: "處理檔案錯誤" };
    }
    // 確認 file 欄位符合規定
    const fileSheetRows = await fileHelper.readExcelFile(buffer);

    // 讀取檔案的 object 是否符合規定
    if (!(fileSheetRows && fileSheetRows[0] && fileSheetRows[0].data)) {
      return { data: null, error: "檔案不符規定" };
    }
    // 目前檔案的欄位
    const cols = fileHelper.getExcelCols(fileSheetRows[0].data);
    // 基本欄位
    const defaultCols = patternSetting.fileCols;
    const colsEqual = fileHelper.checkColsEqual(cols, defaultCols);

    // 檔案中的欄位不符合設定
    if (!colsEqual) {
      return { data: null, error: "檔案欄位不符規定" };
    }

    const rows =
      Array.isArray(fileSheetRows) &&
      fileSheetRows.length > 0 &&
      fileSheetRows[0].data;
    const mapList = fileHelper.arrayToMapList(rows.slice(1), rows[0]);

    // 修改欄位
    const modifiedMapList = mapList.map((row) => {
      const { 起點, 終點, 原始資料, 關係, 主事件分類, 子事件分類 } = row;
      const eventTypeName = !row.子事件分類 ? row.主事件分類 : row.子事件分類;

      const evtTypeId = eventTypeName
        ? state.sna.filter.eventTypes.initial.find(
            (eventType) => eventType.name === eventTypeName
          )?.evtTypeId || ""
        : "";

      return {
        srcId: "",
        srcLabel: 起點,
        dstId: "",
        dstLabel: 終點,
        content: 原始資料,
        eventType: evtTypeId,
        eventTypeName,
        relationship: 關係,
        mainEvn: 主事件分類,
        subEvn: 子事件分類,
      };
    });

    // 生成新的 Excel 檔案
    const newWorkbook = XLSX.utils.book_new();
    const newWorksheet = XLSX.utils.json_to_sheet(modifiedMapList);
    XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, "Sheet1");
    const newBuffer = XLSX.write(newWorkbook, {
      bookType: "xlsx",
      type: "array",
    });

    return { data: modifiedMapList, buffer: newBuffer, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
};

const excelFileReader = (state) => (onSuccess, onError) => {
  const reader = new FileReader();
  reader.onload = async (e) => {
    const data = e.target.result;
    const response = await processExcelFile(data, state);
    if (response.data) {
      onSuccess(response.data, response.buffer);
    }
    if (response.error) {
      onError(response.error);
    }
  };
  return reader;
};

const tsvFileReader = () => (onSuccess, onError) => {
  const reader = new FileReader();
  reader.onload = async (e) => {};
  return reader;
};

const gephiFileReader = () => (onSuccess, onError) => {
  const reader = new FileReader();
  reader.onload = async (e) => {};
  return reader;
};

const ucinetFileReader = () => (onSuccess, onError) => {
  const reader = new FileReader();
  reader.onload = async (e) => {};
  return reader;
};

// gephi 可支援格式：excel,tsv,dl,gdf,gml,graphml,net,vna
// 區分檔案類別, 並分開處理
const getFileReader = (fileType, state) => {
  switch (fileType) {
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return excelFileReader(state);
    case "text/tab-separated-values":
      return tsvFileReader();
    // other type.....
    default:
      return null;
  }
};

const inputFileListener =
  (onSuccess = safeFunc, onError = safeFunc, state) =>
  (event) => {
    event.preventDefault();
    if (event.target.files) {
      const targetFile = event.target.files[0];
      const handleReadFileSuccess = (dataList, newBuffer) => {
        if (onSuccess) onSuccess({ dataList, files: [targetFile], newBuffer });
      };
      const handleReadFileError = (errMessage) => {
        if (onError) onError(errMessage);
      };

      const fileReader = getFileReader(targetFile?.type, state)(
        handleReadFileSuccess,
        handleReadFileError
      );
      fileReader.readAsArrayBuffer(targetFile);
    }
  };

export const readFile = (onSuccess, onError, state) => {
  const inputTag = document.createElement("input");
  inputTag.type = "file";
  inputTag.accept = ".xlsx";
  inputTag.addEventListener(
    "change",
    inputFileListener(onSuccess, onError, state)
  );
  inputTag.click();
};

export const uploadFile = async ({ user, token, files }) => {
  try {
    const res = await singleImportFile({
      files,
      token,
      user,
      pattern: importConfig.pattern.hsna_sna.name,
    });
    const { data } = res;
    return data;
  } catch (err) {
    return err;
  }
};

// 流程: 讀取檔案,檢查檔案=>上傳檔案至後台=>更新 firebase realtime
export const readAndUploadFileApi = async ({
  user,
  token,
  onSuccess,
  onError,
  onUploadStart,
  state,
}) => {
  const onUploadSuccess = async ({ fileName, originalName }) => {
    // 儲存至 files/file-uid/
    const rtUpdatedData = {
      fileName,
      originalName,
      displayName: originalName,
      creationTime: new Date().toISOString(),
    };
    // 更新 firebase realtime
    fireDb.creatDataset({
      userId: user?.uid,
      data: rtUpdatedData,
      onSuccess: (fileUid) => {
        // 儲存至 users/user-uid/files/
        fireDb.updateUserFiles({
          userId: user?.uid,
          fileUid,
          onSuccess,
          onError,
        });
      },
      onError,
    });
  };

  const onUploadError = (err) => {
    if (onError) onError(err);
  };

  const onReadSuccess = async (resObj) => {
    const { dataList, files, newBuffer } = resObj;
    if (onUploadStart) onUploadStart();
    //
    const newFile = new File([newBuffer], files[0].name, {
      type: files[0].type,
    });
    const uploadRes = await uploadFile({ files: [newFile], token, user });
    if (uploadRes instanceof Error) {
      onUploadError(uploadRes);
    } else {
      const { fileName, originalName } = uploadRes;
      onUploadSuccess({ fileName, originalName });
    }
  };

  const onReadError = (errMessage) => {
    // error message
    if (onError) onError({ message: errMessage });
  };
  //
  readFile(onReadSuccess, onReadError, state);
};

export const readAndUploadFileLocal = async () => {};
